import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// accent: colour of the left bar + background tint on hover
const CATEGORIES = [
  { key: 'technology',    label: 'Technology',         accent: '#818cf8', sub: false },
  { key: 'science',       label: 'Science & Research', accent: '#c084fc', sub: false },
  { key: 'finance',       label: 'Finance',            accent: '#34d399', sub: false },
  { key: 'india_finance', label: 'India Finance',      accent: '#94a3b8', sub: true  },
  { key: 'health',        label: 'Health & Fitness',   accent: '#2dd4bf', sub: false },
  { key: 'education',     label: 'Education',          accent: '#60a5fa', sub: false },
  { key: 'space',         label: 'Space',              accent: '#a78bfa', sub: false },
  { key: 'sports',        label: 'Sports',             accent: '#fb923c', sub: false },
  { key: 'fashion',       label: 'Fashion',            accent: '#f472b6', sub: false },
];

const SUPPORT_LINKS = [
  { to: '/ask-us',   label: 'Ask Us'     },
  { to: '/feedback', label: 'Feedback'   },
  { to: '/contact',  label: 'Contact Us' },
  { to: '/support',  label: 'Support Us' },
];

function CategoryLink({ to, label, accent, sub, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <NavLink
      to={to}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={({ isActive }) =>
        isActive
          ? {}
          : hovered
            ? {
                boxShadow: `inset 3px 0 0 ${accent}`,
                backgroundColor: `${accent}18`,
                color: accent,
              }
            : {}
      }
      className={({ isActive }) =>
        `relative flex items-center gap-3 rounded-lg text-[15px] font-semibold transition-all duration-200 ${
          sub ? 'ml-5 px-3 py-2' : 'px-4 py-2.5'
        } ${
          isActive
            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/40 nav-active-pill'
            : 'text-slate-300'
        }`
      }
    >
      {sub && <span className="text-slate-600 text-xs">└</span>}

      <span className="flex-1 leading-tight">{label}</span>

      {/* Animated accent dot */}
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0 transition-all duration-300"
        style={{
          backgroundColor: accent,
          opacity: hovered ? 1 : 0.4,
          transform: hovered ? 'scale(1.6)' : 'scale(1)',
          boxShadow: hovered ? `0 0 6px ${accent}` : 'none',
        }}
      />
    </NavLink>
  );
}

function SupportLink({ to, label, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center px-4 py-2.5 rounded-lg text-[15px] font-semibold transition-all duration-200 ${
          isActive
            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/40'
            : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'
        }`
      }
    >
      {label}
    </NavLink>
  );
}

export default function Sidebar({ onClose }) {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose?.();
  };

  const handleNavClick = () => onClose?.();

  return (
    <aside className="w-64 bg-slate-900 flex flex-col shrink-0 h-full">
      {/* Brand */}
      <div className="px-6 py-5 border-b border-slate-700/60 flex items-center justify-between">
        <div>
          <h1 className="text-white font-extrabold text-2xl tracking-tight flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-indigo-400 animate-pulse" />
            TERAINFO
          </h1>
          <p className="text-indigo-300/70 text-xs font-medium mt-0.5 tracking-wide">Daily Digest Platform</p>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden text-slate-400 hover:text-white p-1"
          aria-label="Close menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        <p className="text-indigo-400 text-xs font-bold uppercase tracking-[0.2em] px-4 pb-2">
          Categories
        </p>
        {CATEGORIES.map(({ key, label, accent, sub }) => (
          <CategoryLink key={key} to={`/dashboard/${key}`} label={label} accent={accent} sub={sub} onClick={handleNavClick} />
        ))}

        <div className="my-4 border-t border-slate-700/60" />

        <p className="text-indigo-400 text-xs font-bold uppercase tracking-[0.2em] px-4 pb-2">
          Support
        </p>
        {SUPPORT_LINKS.map(({ to, label }) => (
          <SupportLink key={to} to={to} label={label} onClick={handleNavClick} />
        ))}

        {isAdmin && (
          <>
            <div className="my-4 border-t border-slate-700/60" />
            <p className="text-indigo-400 text-xs font-bold uppercase tracking-[0.2em] px-4 pb-2">
              Admin
            </p>
            <SupportLink to="/admin/queries" label="Manage Queries" onClick={handleNavClick} />
          </>
        )}
      </nav>

      {/* User footer */}
      <div className="px-3 py-4 border-t border-slate-700/60">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm shrink-0 select-none">
            {user?.name?.[0]?.toUpperCase() ?? '?'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-slate-400 text-xs capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-red-700 hover:text-white transition-colors"
        >
          <span>🚪</span>
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}
